/**
 * Typen und Hilfen der Seite „Brandmeldeanlage" (BM-1, 20.10.2026).
 *
 * Die Typen folgen `GET …/elektro/bma` (BmaEndpoints.cs, Bmastand.cs, BmaInhalt.cs). Gerechnet wird
 * beim Abruf auf dem Server; die Seite schreibt nichts. Vorbehalte, Annahmen und Fachfragen kommen
 * vom Server im selben Wortlaut wie im Heft ELT-BMA (TX-STANDARD S0.5).
 */

import type { Vorbehalt, Vorbehaltsart, Zustand } from '../../muster';

export interface BmaStaende {
  linien: string | null; bauteile: string | null; melderbedarf: string | null; zentralen: string;
}

export interface BmaKennzahlen {
  bmz: number; rwa: number; ringe: number; teilnehmer: number; gruppen: number; abschnitte: number;
  melderErforderlich: number; melderVorhanden: number; raeumeFehlend: number; ringeMitBefund: number;
  warnungen: number; fehler: number; vorgaben: number; vorgabenUnbestaetigt: number;
}

export interface BmaVorbehalt { art: Vorbehaltsart; text: string; verweis: string | null }

export type BmaKapitelart = 'pflicht' | 'wahl' | 'gesamt';

export interface BmaKapitel {
  nummer: string; titel: string; art: BmaKapitelart; einzelausgabe: string | null; quelle: string;
  seite: string | null; befunde: number;
}

export interface BmaZentrale {
  kennzeichen: string; art: string; baureihe: string | null; raum: string | null; geschoss: string | null;
  linien: number; teilnehmer: number; maxRinge: number | null; steckplaetze: number | null;
  maxRingadressen: number | null; maxNetzwerk: number | null; ringmodule: number; ringeAusBestueckung: number;
  einspeisung: boolean; notstromangabe: string | null; bewertung: string;
}

/** BM-5: der Loopplan ELT-LOP einer Zentrale - jüngste Fassung im Register und ihr Zustand (wie Kapitel 9). */
export interface BmaLoopplan {
  zentraleId: string; zentrale: string; gegenstand: string; dokumentnummer: string | null; zustand: string;
}

/** Die drei Zustände eines Loopplans (LoopplanPdf.Zustand*), wortgleich vom Server. */
export const LOOPPLAN_AKTUELL = 'aktuell';
export const LOOPPLAN_FEHLT = 'noch nicht erzeugt';

/** BM-7: eine Unterlage für Inbetriebnahme und Abnahme mit Zustand (wie Kapitel 14). */
export interface BmaUnterlage {
  name: string; art: string | null; dokumentnummer: string | null; zustand: string; quelle: string;
}

/** BM-7: die Wirkprinzipprüfung je Ansteuerung zusammengefasst. */
export interface BmaWirkprinzip {
  ansteuerung: string; wirkung: string; abschnitte: number; objekte: number; herkunft: string;
}

export interface BmaPruefung {
  normsatz: string; unterlagen: BmaUnterlage[]; wirkprinzip: BmaWirkprinzip[]; pruefpunkte: number;
}

/**
 * Der Zustand einer Unterlage als Farbe (mit Text): liegt vor = gut; geändert, teilweise oder fehlend = Warnung;
 * Fremdunterlage und „entsteht bei der Abnahme“ = neutral.
 */
export function unterlagenzustand(zustand: string): Zustand {
  if (zustand.startsWith('Fremdunterlage') || zustand.startsWith('entsteht')) return 'neutral';
  if (zustand.startsWith('liegt vor') && !zustand.includes('fehlen')) return 'gut';
  return 'warnung';
}

/** Welche Berichtsart eine Unterlage als PDF liefert (die übrigen haben keinen Abruf hier). */
export function unterlagenabruf(art: string | null): 'bfs' | 'bnw' | 'mld' | 'feh' | null {
  switch (art) {
    case 'ELT-BFS': return 'bfs';
    case 'ELT-BNW': return 'bnw';
    case 'ELT-MLD': return 'mld';
    case 'ELT-FEH': return 'feh';
    default: return null;
  }
}

export interface BmaKapazitaet {
  zentralen: number; ringe: number; maxRingeJeZentrale: number | null; mindestZentralen: number | null;
  ueberschritten: string[]; entscheidung: string | null;
}

export interface BmaKomponente {
  system: string; bauteilart: string; bezeichnung: string; typ: string | null; anzahl: number; anLinie: number;
  mitAdresse: number; ohneRaum: number; mitDatenblatt: number; besonderheit: string | null; norm: string | null;
}

export interface BmaBesonderheiten {
  rauchmelder: number; sockelsirenen: number; ringeMitSockelsirene: number; rauchmelderAlarmstromMa: number;
  handfeuermelder: number; rwaBedienstellen: number; koppler: number; signalgeber: number;
}

export interface BmaAnnahme {
  parameter: string; wert: string | null; einheit: string | null; herkunft: string; quelle: string | null;
  bestaetigt: boolean; normbezug: string | null;
}

export interface BmaBefund { rang: string; regel: string; gegenstand: string; meldung: string; kapitel: string }

export interface BmaFachfrage {
  nummer: string; prio: string; status: string; frage: string; antwort: string | null; kapitel: string;
  quelle: string; haftungsrelevant: boolean;
}

export interface BmaStand {
  stand: string;
  staende: BmaStaende;
  kennzahlen: BmaKennzahlen;
  vorbehalte: BmaVorbehalt[];
  kapitel: BmaKapitel[];
  zentralen: BmaZentrale[];
  loopplaene: BmaLoopplan[];
  pruefung?: BmaPruefung;
  kapazitaet: BmaKapazitaet;
  komponenten: BmaKomponente[];
  besonderheiten: BmaBesonderheiten;
  annahmen: BmaAnnahme[];
  befunde: BmaBefund[];
  fachfragen: BmaFachfrage[];
}

/** Der Hinweis an den PDF-Knöpfen (F11). */
export const PDF_HINWEIS =
  'Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit eigener Dokumentnummer an; sonst '
  + 'liefert er die abgelegte Fassung (F11). Dieselbe Auswahl ergibt dieselbe Fassung, gleich in welcher Reihenfolge.';

/** Die Vorbehalte des Servers für das Muster `Vorbehalte`. */
export function alsVorbehalte(eintraege: readonly BmaVorbehalt[]): Vorbehalt[] {
  return eintraege.map((v) => ({ art: v.art, text: v.text, verweis: v.verweis ?? undefined }));
}

/**
 * Die Kapitelauswahl als Adresszusatz: in Katalogreihenfolge, ohne Doppel, nur wählbare Kapitel - dieselbe Regel
 * wie auf dem Server (Bmakapitel.Lesen). So ergibt „10, 6" dieselbe Adresse wie „6, 10".
 */
export function auswahlParameter(gewaehlt: readonly string[], kapitel: readonly BmaKapitel[]): string {
  const wahl = new Set(gewaehlt);
  return kapitel.filter((k) => k.art === 'wahl' && wahl.has(k.nummer)).map((k) => k.nummer).join(',');
}

/**
 * Was ein einzelnes Kapitel als PDF liefert: die eigene Berichtsart (ELT-BFS, ELT-BNW, ELT-MLD), sonst ein
 * Teilheft ELT-BMA mit diesem Kapitel und den Pflichtkapiteln.
 */
export function einzelausgabe(k: BmaKapitel): 'bfs' | 'bnw' | 'mld' | 'teilheft' | null {
  if (k.art !== 'wahl') return null;
  switch (k.einzelausgabe) {
    case 'ELT-BFS': return 'bfs';
    case 'ELT-BNW': return 'bnw';
    case 'ELT-MLD': return 'mld';
    // ELT-LOP gibt es je Zentrale (Abschnitt „Loopläne“); das Kapitel selbst ist das Verzeichnis im Teilheft.
    case 'ELT-LOP': return 'teilheft';
    default: return 'teilheft';
  }
}

/** „TT.MM.JJJJ HH:MM UTC" (S1.2) - ohne Laufzeitkultur, die Zeit kommt als UTC vom Server. */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const z = (n: number) => String(n).padStart(2, '0');
  return `${z(d.getUTCDate())}.${z(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} `
    + `${z(d.getUTCHours())}:${z(d.getUTCMinutes())} UTC`;
}

/** „TT.MM.JJJJ" (S1.2, Modellstand). */
export function tag(iso: string | null | undefined): string {
  const t = zeitpunkt(iso);
  return t === '—' ? t : t.slice(0, 10);
}

/** Der Rechenstand im Seitenkopf (S1.2): die drei Stände, aus denen die Seite liest. */
export function rechenstand(s: BmaStaende): string {
  return `Melderbedarf gerechnet ${zeitpunkt(s.melderbedarf)} · Linien ${tag(s.linien)} · Modellstand ${tag(s.bauteile)}`;
}
